import { FeedFile, FileRole, FilesCapability } from '../models/provider.model';

/**
 * The provider descriptor's feed file rules (`capabilities.files`), as core enforces them
 * (core#19): when roles are declared a file needs one of them, a role accepts its own content
 * types (else the capability's), and no file set is delivered until every required role has a
 * file with content.
 */

/** The declared roles; empty = any role (free text). */
export function declaredRoles(files: FilesCapability | null): FileRole[] {
  return files?.roles ?? [];
}

/** Content types a file with this role may have (null role = no role); empty = any. Mirrors
 *  common's `FilesCapability.contentTypesFor`. */
export function contentTypesFor(files: FilesCapability | null, role: string | null): string[] {
  const own = declaredRoles(files).find((r) => r.name === role)?.contentTypes ?? [];
  return own.length ? own : (files?.contentTypes ?? []);
}

/** `accept` for a file picker: the role's content types, else `fallback` (e.g. `.zip,application/zip`). */
export function acceptFor(files: FilesCapability | null, role: string | null, fallback = ''): string {
  const types = contentTypesFor(files, role);
  return types.length ? types.join(',') : fallback;
}

/** Whether a file's role is one the provider accepts (always, when none are declared). */
export function roleDeclared(files: FilesCapability | null, role: string | null): boolean {
  const roles = declaredRoles(files);
  return !roles.length || (!!role && roles.some((r) => r.name === role));
}

/** Each required role and whether a file with that role has content. */
export function requiredRoleStatus(files: FilesCapability | null, feedFiles: FeedFile[]): { name: string; filled: boolean }[] {
  return declaredRoles(files)
    .filter((role) => role.required)
    .map((role) => ({ name: role.name, filled: feedFiles.some((f) => f.role === role.name && !!f.content) }));
}
